import React, { useCallback, useMemo } from 'react';
import { reportError } from '../../../../lib/reportError';
import { formatPrice } from '../../../../lib/formatPrice';
import { useUpdateOrderPaymentMethod } from '../../../../hooks/useUpdateOrderPaymentMethod';
import { useErrorModal } from '../../../../hooks/useErrorModal';
import {
  AdyenPaymentMethodSelector,
  Mode as AdyenPaymentMethodSelectorMode
} from '../../../PaymentMethod/AdyenPaymentMethodSelector';
import { useOrderDetails } from '../../../OrderDetails';

enum Mode {
  Resting,
  Changing
}

interface Props {
  defaultMode?: AdyenPaymentMethodSelectorMode;
  onModeChange: (incomingMode: number) => void;
}

export const OrderDetailsAdyenPaymentMethod: React.FC<Props> = ({ defaultMode, onModeChange }) => {
  const { orderNumber, orderSummary } = useOrderDetails();

  const { updateOrderPaymentMethod } = useUpdateOrderPaymentMethod(orderNumber);
  const { showErrorModal } = useErrorModal();

  const remainingBalance =
    orderSummary && orderSummary.attributes.paymentSummary.remainingBalance.price > 0
      ? formatPrice(
          orderSummary.attributes.paymentSummary.remainingBalance.currency,
          orderSummary.attributes.paymentSummary.remainingBalance.price
        )
      : undefined;
  const payToday =
    orderSummary && orderSummary.attributes.paymentSummary.payToday.price > 0
      ? formatPrice(
          orderSummary.attributes.paymentSummary.payToday.currency,
          orderSummary.attributes.paymentSummary.payToday.price
        )
      : undefined;

  const onSubmit = useCallback(
    selectedPaymentMethodId => {
      return updateOrderPaymentMethod(Number(selectedPaymentMethodId))
        .then(() => onModeChange(Mode.Resting))
        .catch(error => {
          reportError(error);
          showErrorModal();
        });
    },
    [updateOrderPaymentMethod, onModeChange, showErrorModal]
  );

  const descriptionText = useMemo(() => {
    if (!payToday && remainingBalance)
      return `Remaining Balance of ${remainingBalance} will be charged to this credit card`;
    if (payToday) return `${payToday} will be charged to this credit card`;
  }, [payToday, remainingBalance]);

  return (
    <AdyenPaymentMethodSelector
      onSubmit={onSubmit}
      onModeChange={onModeChange}
      description={descriptionText}
      isChangeable={orderSummary?.attributes.isPaymentChangeable}
      defaultMode={defaultMode}
      defaultPaymentMethodId={orderSummary?.attributes.selectedPaymentId}
      defaultPaymentMethod={orderSummary?.attributes.paymentMethod}
    />
  );
};
